// ChatPerson.tsx
import React, {
  useState,
  useEffect,
  useMemo,
  memo,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { HashLoader } from "react-spinners";
import send from "../../assets/images/Send.png";
import LogoutModal from "../../components/LogOutModal";
import { endpoint } from "../../assets/api/endpoint";
import { apiHandler } from "../../assets/api";
import WebSocketInstance from "../../components/WebSocket";

const ChatPerson = ({ chatData, OnMessage }) => {
  const { accessToken, userId, profileData, fetchMinorPermission } = useSelector(
    (state) => state.loginInfo
  );

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, connecting: false });
  const [loader, setLoader] = useState(false);
  const [isMinorModal, setIsMinorModal] = useState(false);

  const messagesEndRef = useRef(null);
  const fetchedChatIdRef = useRef(null);
  const lastRenderedDayRef = useRef(null);

  const receiverId = useMemo(() => {
    if (chatData?.chatType === "PRIVATE" && chatData?.users) {
      const otherUser = chatData.users.find((user) => user.id !== userId);
      return otherUser?.id || null;
    }
    return null;
  }, [chatData, userId]);

  const sortedIds = useMemo(() => {
    if (!userId || !receiverId) return [0, 0];
    return [userId, receiverId].sort((a, b) => a - b);
  }, [userId, receiverId]);

  const destination =
    chatData.chatType === 'PRIVATE'
      ? `/chat/private/${sortedIds[0]}/${sortedIds[1]}`
      : `/chat/group/${chatData.chatId}`;

  useEffect(() => {
    const updateConnectionStatus = () => {
      const info = WebSocketInstance.getConnectionInfo();
      setConnectionStatus({ connected: info.connected, connecting: info.connecting });
    };
    updateConnectionStatus();
    const interval = setInterval(updateConnectionStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  const preprocessMessage = (msg) => {
    const parsedTimestamp = moment(msg.timestamp);
    return {
      ...msg,
      timestamp: parsedTimestamp.valueOf(),
      isOutgoing: msg.senderId === userId,
      displayTime: parsedTimestamp.format("hh:mm A"),
      displayDate: parsedTimestamp.format("DD/MM/YYYY"),
      dayKey: parsedTimestamp.format("YYYY-MM-DD"),
    };
  };

  useEffect(() => {
    const handleIncomingMessage = (message) => {
      console.log('message',message)
      const processed = preprocessMessage(message);

      setMessages((prev) => {
        const exists = prev.some((msg) =>
          msg.senderId === processed.senderId &&
          msg.content === processed.content &&
          Math.abs((msg.timestamp || 0) - processed.timestamp) < 2000
        );
        return exists ? prev : [...prev, processed];
      });
    };

    const setup = async () => {
      try {
        WebSocketInstance.unsubscribe(destination);
        await WebSocketInstance.connect();
        await WebSocketInstance.subscribe(destination, handleIncomingMessage);
      } catch (err) {
        console.error("❌ WebSocket setup error", err);
      }
    };

    setup();

    return () => {
      WebSocketInstance.unsubscribe(destination);
    };
  }, [destination, userId]);

  useEffect(() => {
    if (!chatData?.chatId || !chatData?.chatType || !userId || !accessToken?.accessToken) {
      setMessages([]);
      fetchedChatIdRef.current = null;
      return;
    }

    if (fetchedChatIdRef.current === chatData.chatId) return;

    const fetchChatData = async () => {
      try {
        setLoader(true);
        const pageSize = 50;
        let page = 0;
        let totalPages = 1;
        let allMessages = [];

        while (page < totalPages) {
          let result;
          if (chatData.chatType === "PRIVATE") {
            if (!receiverId) return;
            result = await apiHandler({
              url: `${endpoint.PRIVATE_CHAT}/${chatData.chatId}/${userId}/${receiverId}?page=${page}&size=${pageSize}`,
              method: "GET",
              authToken: accessToken.accessToken,
            });
          } else {
            result = await apiHandler({
              url: `${endpoint.GROUP_CHAT}/${chatData.chatId}/messages?page=${page}&size=${pageSize}`,
              method: "GET",
              authToken: accessToken.accessToken,
            });
          }

          const data = result?.data;
          if (data?.content?.length) {
            allMessages = [...allMessages, ...data.content];
            totalPages = data.totalPages || 1;
            page++;
          } else {
            break;
          }
        }

        allMessages.sort((a, b) => a.messageId - b.messageId);
        const processed = allMessages.map(preprocessMessage);

        OnMessage?.(processed);
        setMessages(processed);
        fetchedChatIdRef.current = chatData.chatId;
      } catch (error) {
        console.error("❌ Error fetching chat data:", error);
      } finally {
        setLoader(false);
      }
    };

    fetchChatData();
  }, [chatData?.chatId, chatData?.chatType, userId, receiverId, accessToken?.accessToken, OnMessage]);

  const handleSendMessage = useCallback(() => {
    if (fetchMinorPermission?.chatAllow === false) {
      setIsMinorModal(true);
      return;
    }

    const trimmedMessage = newMessage.trim();
    if (!trimmedMessage || !connectionStatus.connected) return;

    const messagePayload = {
      id: `temp-${Date.now()}`,
      senderId: userId,
      content: trimmedMessage,
     timestamp: new Date().toISOString(),
      ...(chatData.chatType === "PRIVATE"
        ? { receiverId }
        : { groupId: chatData.chatId }),
    };

    const sendDestination =
      chatData.chatType === "PRIVATE"
        ? `/app/chat/private/${receiverId}/${userId}`
        : `/app/chat/group/${chatData.chatId}`;

    const trySend = async () => {
      try {
        await WebSocketInstance.connect();
        WebSocketInstance.send(sendDestination, messagePayload);
        setNewMessage("");
      } catch (error) {
        console.error("❌ Send error:", error);
      }
    };

    trySend();
  }, [
    fetchMinorPermission?.chatAllow,
    newMessage,
    connectionStatus.connected,
    userId,
    chatData.chatType,
    chatData.chatId,
    receiverId,
    profileData?.baseProfile?.name,
  ]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const statusDisplay = connectionStatus.connected
    ? { text: "Connected", color: "green" }
    : connectionStatus.connecting
      ? { text: "Connecting...", color: "orange" }
      : { text: "Disconnected", color: "red" };

  return (
    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100%" }}>
      <div className="card chat-detail-image" style={{ width: "100%", height: "100%", borderRadius: "20px", overflow: "hidden" }}>
        {!loader ? (
          <>
            <div className="card-header bg-white d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid #ddd", padding: "10px 15px" }}>
              <h5 className="mb-0" style={{ fontWeight: "bold" }}>
                {chatData?.chatType === "PRIVATE"
                  ? chatData?.users?.find((u) => u.id !== userId)?.avatarName || "User"
                  : chatData?.chatName}
              </h5>
            </div>

            <div className="chat-body-container" style={{ position: "relative", height: "100%", width: "100%" }}>
              <div className="card-body chat-person-wrapper" style={{ position: "absolute", width: "100%", height: "90%", overflowY: "auto", padding: "15px" }}>
                {messages.map((message, index) => {
                  const isUser = message.senderId === userId;
                  const showDateHeader = index === 0 || message.dayKey !== messages[index - 1].dayKey;

                  return (
                    <React.Fragment key={`${message.id || index}-${message.timestamp}`}>
                      {showDateHeader && (
                        <div className="d-flex justify-content-center">
                          <div className="text-center text-muted my-2 chat-date" style={{ fontSize: "0.85rem" }}>
                            {message.displayDate}
                          </div>
                        </div>
                      )}
                      <div className={`d-flex ${isUser ? "justify-content-end" : "justify-content-start"} mb-1`} style={{ marginTop: "8px" }}>
                        <div className="p-2" style={{
                          backgroundColor: isUser ? "#007bff" : "#f0f0f0",
                          color: isUser ? "white" : "black",
                          borderRadius: "10px",
                          maxWidth: "70%",
                        }}>
                          <div className="chat-user-name" style={{ textAlign: isUser ? "right" : "left", color: isUser ? "#e0e0e0" : "#888" }}>
                            {isUser ? "You" : message?.senderName}
                          </div>
                          <div>{message?.content}</div>
                          <div style={{
                            fontSize: "0.75rem",
                            color: isUser ? "#e0e0e0" : "#888",
                            textAlign: isUser ? "right" : "left",
                            marginTop: "4px",
                          }}>
                            {message.displayTime}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

                <div ref={messagesEndRef} />
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex align-items-center justify-content-center" style={{ height: "55vh" }}>
            <HashLoader color="#e48181" />
          </div>
        )}

        <div className="card-footer bg-white d-flex align-items-center" style={{ zIndex: 33333 }}>
          <input
            type="text"
            className="form-control"
            placeholder={connectionStatus.connected ? "Type a message..." : "Connecting..."}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ borderRadius: "20px", marginRight: "10px" }}
            disabled={!connectionStatus.connected}
          />
          <img
            src={send}
            onClick={handleSendMessage}
            alt="Send"
            style={{
              opacity: connectionStatus.connected ? 1 : 0.5,
              cursor: connectionStatus.connected ? "pointer" : "not-allowed",
              filter: connectionStatus.connected ? "none" : "grayscale(100%)",
            }}
          />
        </div>

        {isMinorModal && (
          <LogoutModal
            show={isMinorModal}
            minorPermission={true}
            onCancel={() => setIsMinorModal(false)}
            text="Permission is not for chat, Please take the permission from guardian"
          />
        )}
      </div>
    </div>
  );
};

export default memo(ChatPerson);
